import {
  LDContext,
  useFlags,
  useLDClient,
  useLDClientError,
} from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useRef, useState } from "react";
import isEqual from "lodash/isEqual";
import { buildContext, clientSideID, INITIAL_CONTEXT } from ".";
import { LaunchDarklyFeatureFlags, parseFeatureFlags } from "./utils";

const IDENTIFY_TIMEOUT_MS = 2000;

const DEFAULT_FEATURES = parseFeatureFlags({});

export interface IdentifiedUser {
  key: string;
  email?: string;
  name?: string;
}

const isAnonymous = (context: LDContext | undefined) =>
  !!context && "anonymous" in context && context.anonymous === true;

export const useLaunchDarkly = (user: IdentifiedUser | null) => {
  const client = useLDClient();
  const clientError = useLDClientError();
  const flags = useFlags<LaunchDarklyFeatureFlags>();

  const key = user?.key;
  const email = user?.email;
  const name = user?.name;
  // Derived in render so a sign-in reads unidentified in its own commit.
  const target = useMemo(
    () => (key ? buildContext({ key, email, name }) : INITIAL_CONTEXT),
    [key, email, name],
  );

  const [identifiedContext, setIdentifiedContext] = useState<LDContext | null>(
    null,
  );
  const [identificationError, setIdentificationError] = useState<Error | null>(
    null,
  );
  // The provider never clears an init-timeout error, even when init later
  // succeeds; recovery is tracked separately so a slow init is not permanent.
  const [initRecovered, setInitRecovered] = useState(false);
  // Serialized: the SDK applies whichever identify settles last.
  const identifyChain = useRef<Promise<unknown>>(Promise.resolve());
  const pendingIdentifies = useRef(0);

  const effectiveClientError = initRecovered ? undefined : clientError;

  useEffect(() => {
    if (!client || !clientError) {
      return;
    }
    let cancelled = false;
    // Resolves only on successful init; a genuine failure rejects.
    client
      .waitForInitialization()
      .then(() => {
        if (!cancelled) setInitRecovered(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [client, clientError]);

  useEffect(() => {
    if (!client || effectiveClientError) {
      return;
    }
    const held = client.getContext();
    // The SDK injects a generated key into keyless anonymous contexts, so the
    // anonymous target matches on anonymity alone; the held context is stale
    // while an identify is in flight, so it is only trusted when idle.
    const heldMatches =
      target === INITIAL_CONTEXT ? isAnonymous(held) : isEqual(held, target);
    if (pendingIdentifies.current === 0 && heldMatches) {
      setIdentifiedContext(target);
      setIdentificationError(null);
      return;
    }

    let cancelled = false;
    setIdentificationError(null);
    // Only unblocks the UI; the identify keeps running and applies late.
    const timer = setTimeout(() => {
      if (!cancelled) {
        setIdentificationError(
          new Error("LaunchDarkly identification timed out"),
        );
      }
    }, IDENTIFY_TIMEOUT_MS);
    pendingIdentifies.current += 1;
    const identifyCurrent = identifyChain.current.then(() =>
      client.identify(target),
    );
    identifyChain.current = identifyCurrent.catch(() => {});
    identifyCurrent
      .then(() => {
        if (!cancelled) {
          setIdentifiedContext(target);
          setIdentificationError(null);
        }
      })
      .catch((error) => {
        if (!cancelled) setIdentificationError(error);
      })
      .finally(() => {
        pendingIdentifies.current -= 1;
        clearTimeout(timer);
      });
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [target, client, effectiveClientError]);

  const identified =
    identifiedContext !== null && isEqual(identifiedContext, target);

  // Unconfirmed contexts get defaults: the SDK still holds the previous
  // context's flags, which must not leak through the error render path.
  const features = useMemo(
    () => (identified ? parseFeatureFlags(flags) : DEFAULT_FEATURES),
    [identified, flags],
  );

  return {
    features,
    configured: !!clientSideID,
    identified,
    error: effectiveClientError || identificationError,
  };
};
